// mission-status-label-after-run.ts
//
// Which `mission_status_changed` label a finished `run` journals (split out of
// supervisor-mission-run.ts, which sat at the 200-line budget):
//   - gate-blocked ⇒ `blocked:<code>` (Bug I, P15);
//   - ticket loop `done` ⇒ the release state (H14, one label per state);
//   - X4 (close-out 2026-09-30): otherwise, ONLY when the current label is a
//     stale gate block, the label of how this run ended — `status` used to
//     keep `blocked:gate_not_approved` after the gate was approved and the
//     run stopped for another reason. Any other label (WAITING_HUMAN from a
//     cost ceiling, ABORTED, interrupted, …) was written for a reason this run
//     did not clear, so it is left alone.
import { missionStatusLabelForReleaseFlow } from "./release-flow-resume-observability.js";
import type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume-outcome.js";
import type { RunUntilIdleOutcome } from "./supervisor-run-until-idle-types.js";

const GATE_BLOCK_LABELS = new Set([
  "blocked:gate_not_approved",
  "blocked:gate_state_unreadable",
  "blocked:interview_unanswered",
  "blocked:g2_plan_not_ready",
]);

/**
 * Outcomes whose own label is not written anywhere else. NOT `paused`
 * (review P4 H1): pause lives in pause-state.json and `status` overlays it at
 * read time — a journaled `paused` would outlive the `resume`.
 */
const OUTCOME_LABELS: Partial<Record<RunUntilIdleOutcome, string>> = {
  blocked: "blocked",
  idle: "idle",
  quota_sleeping: "quota_sleeping",
};
/** Labels this function may replace: stale gate blocks and its own outcome labels (review P4 M1). */
const REPLACEABLE_LABELS = new Set([...GATE_BLOCK_LABELS, ...Object.values(OUTCOME_LABELS)]);

export function missionStatusLabelAfterRun(input: {
  currentStatus: string | undefined;
  outcome: RunUntilIdleOutcome;
  gateBlocked: { code: string } | undefined;
  releaseFlow: ReleaseFlowResumeOutcome | undefined;
}): string | undefined {
  if (input.gateBlocked) return `blocked:${input.gateBlocked.code}`;
  if (input.outcome === "done") return missionStatusLabelForReleaseFlow(input.releaseFlow);
  if (input.currentStatus === undefined || !REPLACEABLE_LABELS.has(input.currentStatus)) {
    return undefined;
  }
  const next = OUTCOME_LABELS[input.outcome];
  return next === input.currentStatus ? undefined : next;
}
